import { useEffect, useRef, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { NicolAIAvatar } from '@/shared/components'
import { ChatMessage, type ChatMessageData } from './chat-message'

interface ChatMessagesProps {
  messages: ChatMessageData[]
  isLoading?: boolean
}

/**
 * Chat messages container with auto-scroll
 * Design reference: Pencil Node ID BtoIx
 */
export const ChatMessages = memo(function ChatMessages({
  messages,
  isLoading = false,
}: ChatMessagesProps) {
  const { t } = useTranslation()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, isLoading])

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-y-auto bg-background-secondary p-6"
    >
      <div className="mx-auto max-w-[900px] space-y-6">
        {/* Welcome message if no messages yet */}
        {messages.length === 0 && (
          <div className="flex gap-3">
            <NicolAIAvatar size="lg" />
            <div className="max-w-[900px] rounded-2xl rounded-tl-none border border-border bg-white p-4">
              <p className="text-sm text-foreground">
                {t('scopingV2.chat.welcomeMessage')}
              </p>
            </div>
          </div>
        )}

        {/* Messages list */}
        {messages.map((message) => (
          <ChatMessage key={message.id} message={message} />
        ))}

        {/* Loading indicator */}
        {isLoading && (
          <div className="flex gap-3">
            <NicolAIAvatar size="lg" />
            <div className="rounded-2xl rounded-tl-none border border-border bg-white p-4">
              <div className="flex items-center gap-1">
                <span
                  className="h-2 w-2 animate-bounce rounded-full bg-foreground-muted"
                  style={{ animationDelay: '0ms' }}
                />
                <span
                  className="h-2 w-2 animate-bounce rounded-full bg-foreground-muted"
                  style={{ animationDelay: '150ms' }}
                />
                <span
                  className="h-2 w-2 animate-bounce rounded-full bg-foreground-muted"
                  style={{ animationDelay: '300ms' }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </div>
    </div>
  )
})
